<template>
    <div class="columns is-centered is-multiline">
        <div class="column is-12">
            <QueryInput
                v-model="searchQuery"
                :search-query-input-class="searchQueryInputClass"
                @update:modelValue="onSearchQueryChange"
                :search-refreshing="searchRefreshing"
            />
        </div>

        <template v-if="initDone">
            <div class="column is-12">
                <table class="table is-hoverable is-fullwidth">
                    <thead>
                        <tr>
                            <th>
                                <span class="icon">
                                    <i class="fa fa-clock" />
                                </span>
                                <span>
                                    {{ translate("admin_verification.time") }}
                                </span>
                            </th>
                            <th>
                                {{ translate("common.first_name_and_last_name") }}
                            </th>
                            <th>
                                {{ translate("common.academy") }}
                            </th>
                            <th>
                                {{ translate("common.category") }}
                            </th>
                            <th>
                                {{ translate("admin_verification.action") }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="verificationLog in verificationLogs"
                            :key="verificationLog.logId"
                            @mouseover="highlightId(verificationLog.eventRegistrationId)"
                            @mouseleave="highlightId(0)"
                        >
                            <td>
                                <span :class="getClassForName(verificationLog.eventRegistrationId)">
                                    {{ convertToTimezone(verificationLog.time, store.eventTimezone!) }}
                                </span>
                            </td>
                            <td>
                                <span :class="[getClassForName(verificationLog.eventRegistrationId), 'clarity-mask']">
                                    {{ verificationLog.firstName }} {{ verificationLog.lastName }}
                                </span>
                            </td>
                            <td>
                                <span :class="getClassForName(verificationLog.eventRegistrationId)">
                                    {{ verificationLog.academyName }} / {{ verificationLog.branch }}
                                </span>
                            </td>
                            <td>
                                <span :class="getClassForName(verificationLog.eventRegistrationId)">
                                    {{ verificationLog.category }}
                                </span>
                            </td>
                            <td>
                                <VerificationStatus :verification-state="verificationLog.verificationState" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="verificationLogs.length === 0" class="column is-6 is-offset-3">
                <div class="notification is-warning is-light has-text-centered">
                    <span class="icon">
                        <i class="fa fa-search" />
                    </span>
                    <span>
                        {{ translate("admin_verification.no_results") }}
                    </span>
                </div>
            </div>
        </template>
        <div v-else class="column is-12">
            <LoadingSpinner2 class="has-added-margin" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from "vue";
    import VerificationStatus from "./VerificationStatus.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import debounce from "lodash.debounce";
    import history from "../Common/history";
    import { useVerificationStore } from "./verificationStore";
    import verificationsApi, { VerificationLog } from "../Api/verifications";

    const historyInstance = history.getInstance(window);

    const store = useVerificationStore();

    const initDone = ref(false);
    const highlightedId = ref(0);
    const searchQuery = ref("");
    const searchRefreshing = ref(false);
    const verificationLogs = ref<VerificationLog[]>([]);

    const searchQueryInputClass = computed(() => {
        if (searchQuery.value.length > 0 && verificationLogs.value.length === 0) {
            return "is-danger";
        }

        return "";
    });

    watch(searchQuery, () => {
        historyInstance.setQueryParam("historyQuery", searchQuery.value);
    });

    onMounted(() => {
        initDone.value = false;
        searchQuery.value = historyInstance.getQueryParam("historyQuery") || "";

        return refreshLogs(searchQuery.value)
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    });

    function refreshLogs(query: string = "") {
        searchRefreshing.value = true;
        return verificationsApi
            .getVerificationLogs(store.eventId!, query)
            .then(logs => (verificationLogs.value = logs))
            .finally(() => {
                searchRefreshing.value = false;
            });
    }

    const onSearchQueryChange = debounce(function () {
        refreshLogs(searchQuery.value).catch(handleError);
    }, 250);

    function getClassForName(eventRegistrationId: number) {
        if (eventRegistrationId === highlightedId.value) {
            return "has-text-highlight";
        }
        return "";
    }

    function highlightId(eventRegistrationId: number) {
        highlightedId.value = eventRegistrationId;
    }
</script>

<style scoped></style>
